import { c as _c } from "react/compiler-runtime";
import { useSpring } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SegmentedControl from '../segmented-control';
import EditMode from './components/edit-mode';
import PreviewMode from './components/preview-mode';
export type MarkdownEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  height?: number;
  expandedHeight?: number;
  expanded?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Sits on the same row as the Edit/Preview control, which then moves to the far right. */
  label?: React.ReactNode;
};
export type MarkdownEditorRef = {
  focus: () => void;
};
type ViewMode = 'edit' | 'preview';
const DEFAULT_HEIGHT = 200;
const EXPANDED_HEIGHT_MULTIPLIER = 3;
const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>((t0, ref) => {
  const $ = _c(41);
  const {
    value: t1,
    onChange,
    height: t2,
    expandedHeight: expandedHeightProp,
    disabled: t3,
    placeholder,
    hasError: t4,
    onFocus,
    onBlur,
    expanded: t5,
    label
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const height = t2 === undefined ? DEFAULT_HEIGHT : t2;
  const disabled = t3 === undefined ? false : t3;
  const hasError = t4 === undefined ? false : t4;
  const expanded = t5 === undefined ? false : t5;
  const textareaRef = useRef(null);
  const [viewMode, setViewMode] = useState("edit");
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const expandedHeight = expandedHeightProp ?? height * EXPANDED_HEIGHT_MULTIPLIER;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => ({
      focus: () => {
        textareaRef.current?.focus();
      }
    });
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  useImperativeHandle(ref, t6);
  const defaultHeight = expanded ? expandedHeight : height;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      stiffness: 300,
      damping: 30
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const animatedHeight = useSpring(defaultHeight, t7);
  const [currentHeight, setCurrentHeight] = useState(defaultHeight);
  const shouldShowExpandButton = (isHovered || isFocused) && viewMode === "edit";
  let t8;
  let t9;
  if ($[2] !== animatedHeight || $[3] !== expandedHeight || $[4] !== height || $[5] !== isExpanded) {
    t8 = () => {
      const targetHeight = isExpanded ? expandedHeight : height;
      animatedHeight.set(targetHeight);
    };
    t9 = [isExpanded, height, expandedHeight, animatedHeight];
    $[2] = animatedHeight;
    $[3] = expandedHeight;
    $[4] = height;
    $[5] = isExpanded;
    $[6] = t8;
    $[7] = t9;
  } else {
    t8 = $[6];
    t9 = $[7];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[8] !== animatedHeight) {
    t10 = () => {
      const unsubscribe = animatedHeight.on("change", latest => {
        setCurrentHeight(Math.round(latest));
      });
      return unsubscribe;
    };
    t11 = [animatedHeight];
    $[8] = animatedHeight;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let t12;
  if ($[11] !== onChange) {
    t12 = e => {
      onChange?.(e.target.value);
    };
    $[11] = onChange;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const handleChange = t12;
  let t13;
  if ($[13] !== onFocus) {
    t13 = () => {
      setIsFocused(true);
      onFocus?.();
    };
    $[13] = onFocus;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const handleFocus = t13;
  let t14;
  if ($[15] !== onBlur) {
    t14 = () => {
      setIsFocused(false);
      onBlur?.();
    };
    $[15] = onBlur;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const handleBlur = t14;
  let t15;
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsHovered(true);
    t16 = () => setIsHovered(false);
    $[17] = t15;
    $[18] = t16;
  } else {
    t15 = $[17];
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [{
      label: "Edit",
      value: "edit"
    }, {
      label: "Preview",
      value: "preview"
    }];
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== viewMode) {
    t18 = <SegmentedControl aria-label="Editor mode" size="compact" options={t17} value={viewMode} onChange={setViewMode} />;
    $[20] = viewMode;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== label || $[23] !== t18) {
    t19 = <div className="flex items-center justify-between gap-2">{label}{t18}</div>;
    $[22] = label;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== currentHeight || $[26] !== disabled || $[27] !== expandedHeight || $[28] !== handleBlur || $[29] !== handleChange || $[30] !== handleFocus || $[31] !== hasError || $[32] !== isExpanded || $[33] !== placeholder || $[34] !== shouldShowExpandButton || $[35] !== value || $[36] !== viewMode) {
    t20 = <div className="flex flex-col w-full">{viewMode === "edit" ? <EditMode textareaRef={textareaRef} value={value} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} disabled={disabled} placeholder={placeholder} hasError={hasError} currentHeight={currentHeight} shouldShowExpandButton={shouldShowExpandButton} isExpanded={isExpanded} onToggleExpand={() => setIsExpanded(!isExpanded)} /> : <PreviewMode value={value} height={currentHeight} maxHeight={expandedHeight} hasError={hasError} />}</div>;
    $[25] = currentHeight;
    $[26] = disabled;
    $[27] = expandedHeight;
    $[28] = handleBlur;
    $[29] = handleChange;
    $[30] = handleFocus;
    $[31] = hasError;
    $[32] = isExpanded;
    $[33] = placeholder;
    $[34] = shouldShowExpandButton;
    $[35] = value;
    $[36] = viewMode;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t19 || $[39] !== t20) {
    t21 = <div className="flex flex-col gap-2 w-full" onMouseEnter={t15} onMouseLeave={t16}>{t19}{t20}</div>;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return t21;
});
MarkdownEditor.displayName = 'MarkdownEditor';
export default MarkdownEditor;